"use client";

import {
  IconBrandGolang,
  IconBrandJavascript,
  IconBrandPhp,
  IconBrandPython,
  IconBrandRust,
  IconBrandTypescript,
  IconCoffee,
  IconDiamond,
  IconTerminal,
} from "@tabler/icons-react";
import { registerDocsHighlighting } from "src/lib/DocsHighlighting";
import {
  type DocsLanguage,
  type DocsLanguageFallback,
  getMergedDocsLanguage,
  mergeIdenticalScriptTabs,
  resolveDocsLanguage,
} from "src/lib/DocsLanguage";
import { useExtracted } from "#i18n";
import DocsCodeTabs, { type DocsTabCode } from "./DocsCodeTabs";
import { useDocsLanguage } from "./DocsLanguageContext";

export type { DocsTabCode } from "./DocsCodeTabs";

const TAB_ICONS: Record<DocsLanguage, typeof IconTerminal> = {
  "ts-sdk": IconBrandTypescript,
  "ts-app-sdk": IconBrandTypescript,
  "js-sdk": IconBrandJavascript,
  "js-app-sdk": IconBrandJavascript,
  python: IconBrandPython,
  go: IconBrandGolang,
  rust: IconBrandRust,
  ruby: IconDiamond,
  java: IconCoffee,
  php: IconBrandPhp,
  curl: IconTerminal,
  fetch: IconBrandJavascript,
  "python-http": IconBrandPython,
  "go-http": IconBrandGolang,
};

const sameCode = (a: readonly DocsTabCode[] | undefined, b: readonly DocsTabCode[] | undefined) =>
  Boolean(a && b) &&
  a!.length === b!.length &&
  a!.every(
    (block, index) =>
      block.value === b![index]!.value &&
      block.companion === b![index]!.companion &&
      block.heading?.href === b![index]!.heading?.href,
  );

export interface DocsLanguageTabsProps {
  /** The languages the example offers, in tab order. */
  tabs: readonly DocsLanguage[];
  /** The code under each tab. Only the selected tab's code is rendered. */
  code: Partial<Record<DocsLanguage, DocsTabCode[]>>;
  /** Build-time tokens for the code, from `encodeDocsHighlighting`. */
  highlighting?: string;
  /** Which tab a language the example lacks selects (see DOCS_LANGUAGE_FALLBACKS). */
  fallback: DocsLanguageFallback;
  /** A link at the end of the tab bar, such as the way to the SDKs the tabs show. */
  link?: { href: string; label: string };
  className?: string;
  [dataAttribute: `data-${string}`]: string | undefined;
}

/**
 * The language tabs of a docs code example, following the reader's docs language. Server
 * components (SdkExample, RequestPreview) resolve the example and pass in the code of every tab
 * with its build-time tokens, so the example registries stay on the server.
 */
const DocsLanguageTabs = (props: DocsLanguageTabsProps) => {
  const { tabs: offered, code, highlighting, fallback, link, className, ...dataAttributes } = props;
  registerDocsHighlighting(highlighting);
  const translateMessage = useExtracted();
  const { language, selectLanguage } = useDocsLanguage();
  // TypeScript examples are usually the JavaScript ones; one tab shows both when they match.
  const { tabs, merged } = mergeIdenticalScriptTabs(offered, (javascript, typescript) =>
    sameCode(code[javascript], code[typescript]),
  );
  const reading = getMergedDocsLanguage(language, merged);
  const selected = resolveDocsLanguage(reading, tabs, fallback);
  const labels: Record<DocsLanguage, string> = {
    "ts-sdk": merged.has("ts-sdk")
      ? translateMessage("JS/TS Server SDK")
      : translateMessage("TS Server SDK"),
    "ts-app-sdk": merged.has("ts-app-sdk")
      ? translateMessage("JS/TS App SDK")
      : translateMessage("TS App SDK"),
    "js-sdk": translateMessage("JS Server SDK"),
    "js-app-sdk": translateMessage("JS App SDK"),
    python: translateMessage("Python SDK"),
    go: translateMessage("Go SDK"),
    rust: translateMessage("Rust SDK"),
    ruby: translateMessage("Ruby SDK"),
    java: translateMessage("Java SDK"),
    php: translateMessage("PHP SDK"),
    curl: translateMessage("cURL"),
    fetch: translateMessage("JS Fetch"),
    "python-http": translateMessage("Python HTTP"),
    "go-http": translateMessage("Go HTTP"),
  };

  return (
    <DocsCodeTabs
      tabs={tabs.map((tab) => ({ value: tab, label: labels[tab], icon: TAB_ICONS[tab] }))}
      selected={selected}
      // A JavaScript reader choosing the shared JS/TS tab keeps their own language.
      onSelect={(value, tabList) => selectLanguage(value === reading ? language : value, tabList)}
      code={code}
      link={link}
      className={className}
      {...dataAttributes}
      data-docs-language={selected === reading ? language : selected}
    />
  );
};

export default DocsLanguageTabs;
